import { useTranslation } from 'react-i18next';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { Divider, Flex, Typography } from 'antd';

type ChecklistProps = {
  actualSidesCount: number;
  sessionsSidesCount: number;
  lowestResolution: number;
  minimalDpi: number;
  scalingPercentage: number;
  isAspectRatioDistorted: boolean;
  hasOverprint?: boolean;
  colorCount?: {
    actualCount: number;
    status: 'perfect' | 'warning' | 'error';
    isValid: boolean;
    requirement?: {
      type: 'max' | 'min' | 'exactly';
      value: string;
    };
  };
};

type CheckItem = {
  label: string;
  isValid: boolean;
  status?: 'perfect' | 'warning' | 'error';
};

export default function QualityChecklist({
  actualSidesCount,
  sessionsSidesCount,
  lowestResolution,
  minimalDpi,
  scalingPercentage,
  isAspectRatioDistorted,
  hasOverprint = false,
  colorCount,
}: ChecklistProps) {
  const { t } = useTranslation();

  // const pageDifference = sessionsSidesCount - actualSidesCount;
  // const getPageCountMessage = () => {
  //   if (pageDifference === 0) {
  //     return t('QualityCheck.PageCountCorrect');
  //   } else if (pageDifference > 0) {
  //     return t('QualityCheck.AddPages', { count: pageDifference });
  //   } else {
  //     return t('QualityCheck.RemovePages', { count: Math.abs(pageDifference) });
  //   }
  // };

  const isScalingPerfect = scalingPercentage >= 99.9 && scalingPercentage <= 100.1;
  const getScalingMessage = () => {
    if (isScalingPerfect) {
      return t('QualityCheck.ScalingPerfect');
    } else if (isAspectRatioDistorted) {
      return t('QualityCheck.ScalingDistorted');
    } else {
      return t('QualityCheck.ScalingProportional');
    }
  };

  const getScalingStatus = (): 'perfect' | 'warning' | 'error' => {
    if (isScalingPerfect) return 'perfect';
    if (isAspectRatioDistorted) return 'error';
    return 'warning';
  };

  const getColorCountMessage = () => {
    if (!colorCount) return '';
    const count = colorCount.actualCount;

    // Format requirement with translation
    let requirementText = '';
    if (colorCount.requirement) {
      const { type, value } = colorCount.requirement;
      if (type === 'max') {
        requirementText = `${t('QualityCheck.Max')} ${value}`;
      } else if (type === 'min') {
        requirementText = `${t('QualityCheck.Min')} ${value}`;
      } else {
        requirementText = `${t('QualityCheck.Exactly')} ${value}`;
      }
    }

    if (colorCount.status === 'perfect') {
      return t('QualityCheck.ColorCountCorrect', { count, requirement: requirementText });
    } else if (colorCount.status === 'warning') {
      return t('QualityCheck.ColorCountWarning', { count, requirement: requirementText });
    } else {
      return t('QualityCheck.ColorCountError', { count, requirement: requirementText });
    }
  };

  const checkItems: CheckItem[] = [
    // {
    //   label: getPageCountMessage(),
    //   isValid: actualSidesCount === sessionsSidesCount,
    // },
    {
      label: getScalingMessage(),
      isValid: isScalingPerfect,
      status: getScalingStatus(),
    },
    {
      label:
        lowestResolution >= minimalDpi || lowestResolution === -1
          ? t('QualityCheck.ResolutionSufficient')
          : t('QualityCheck.ResolutionTooLow'),
      isValid: lowestResolution >= minimalDpi || lowestResolution === -1,
    },
    ...(colorCount
      ? [
          {
            label: getColorCountMessage(),
            isValid: colorCount.isValid,
            status: colorCount.status,
          },
        ]
      : []),
    ...(hasOverprint
      ? [
          {
            label: t('QualityCheck.OverprintDetected'),
            isValid: true, // Not an error, just information
            status: 'warning' as const,
          },
        ]
      : []),
  ];

  const hasFailedChecks = checkItems.some((item) => !item.isValid);

  return (
    <div>
      <Divider>
        <Flex justify="space-between" align="center">
          <Typography.Text strong>{t('QualityCheck.Title')}</Typography.Text>
        </Flex>
      </Divider>

      <Flex vertical gap={4}>
        {checkItems.map((item, index) => {
          const getIconAndColor = () => {
            // Special case for overprint - use printer icon
            if (item.label === t('QualityCheck.OverprintDetected')) {
              return {
                icon: <PrinterOutlined style={{ color: '#faad14', fontSize: '1rem' }} />,
                color: '#faad14',
                fontWeight: 'normal' as const,
              };
            }

            if (item.status === 'perfect' || (item.isValid && !item.status)) {
              return {
                icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '1rem' }} />,
                color: '#52c41a',
                fontWeight: 'normal' as const,
              };
            } else if (item.status === 'warning') {
              return {
                icon: <CheckCircleOutlined style={{ color: '#faad14', fontSize: '1rem' }} />,
                color: '#faad14',
                fontWeight: 'normal' as const,
              };
            } else {
              return {
                icon: <CloseCircleOutlined style={{ color: '#cf1322', fontSize: '1rem' }} />,
                color: '#cf1322',
                fontWeight: 'bold' as const,
              };
            }
          };

          const { icon, color, fontWeight } = getIconAndColor();

          return (
            <Flex key={index} justify="flex-start" align="center" gap={8}>
              {icon}
              <Typography.Text
                style={{
                  fontSize: '0.9rem',
                  color,
                  fontWeight,
                }}
              >
                {item.label}
              </Typography.Text>
              {item.label === t('QualityCheck.OverprintDetected') && (
                <InfoCircleOutlined
                  style={{
                    fontSize: '0.9rem',
                    color: '#faad14',
                    cursor: 'help',
                  }}
                  title={t('QualityCheck.OverprintHint') as string}
                />
              )}
            </Flex>
          );
        })}
      </Flex>
    </div>
  );
}
